import React from "react";
import {
  Modal,
  Button,
  Grid,
  Switch,
  Divider,
  Stack,
  ScrollArea,
  Paper,
  Text,
  Group,
  Select,
  Center,
  Loader,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import ModalHeader from "components/ModalHeader";
import { useUpdateRolePermissions } from "api/role/updateRole";
import { useUpdateRoleHierarchy } from "api/role/updateRole";
import { getPermissionLabel, getPermissionCategoryLabel } from "utils/permissionLabels";
import { RolePermission } from "api/role/addRole";
import { useGetPermissionSet } from "api/permission/getPermissionSet";

const permissionSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.boolean(),
    z.record(permissionSchema),
  ])
);

const editorSchema = z.object({
  permissions: z.record(permissionSchema),
  parentRole: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof editorSchema>;

/**
 * Merge permission set structure with role's current permissions
 * Ensures all available permissions are shown, using role values where they exist
 */
const mergePermissions = (
  permissionSetStructure: Record<string, any>,
  rolePermissions: Record<string, any>
): Record<string, any> => {
  const merge = (
    source: Record<string, any>,
    target: Record<string, any>
  ): Record<string, any> => {
    const result: Record<string, any> = {};

    Object.entries(source).forEach(([key, value]) => {
      if (typeof value === "boolean") {
        // For boolean values, use role's value if exists, otherwise use default
        const hasKeyInTarget = Object.prototype.hasOwnProperty.call(target, key);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        result[key] = hasKeyInTarget ? target[key] : value;
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        // For nested objects, recursively merge
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const targetValue = Object.prototype.hasOwnProperty.call(target, key)
          ? target[key]
          : {};
        const targetObj =
          typeof targetValue === "object" && targetValue !== null
            ? (targetValue as Record<string, any>)
            : {};
        result[key] = merge(
          value as Record<string, any>,
          targetObj
        );
      }
    });

    return result;
  };

  return merge(permissionSetStructure, rolePermissions);
};

interface PermissionEditorProps {
  role: RolePermission | null;
  opened: boolean;
  onClose: () => void;
  allRoles?: RolePermission[];
}

export default function PermissionEditor({
  role,
  opened,
  onClose,
  allRoles = [],
}: PermissionEditorProps) {
  const { mutate, isLoading } = useUpdateRolePermissions();
  const { mutate: updateHierarchy, isLoading: isHierarchyLoading } = useUpdateRoleHierarchy();
  const { data: permissionSetData, isLoading: isPermissionSetLoading } = useGetPermissionSet();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const completePermissions = permissionSetData?.data?.permissions
    ? mergePermissions(permissionSetData.data.permissions, role?.permissions ?? {})
    : role?.permissions ?? {};

  const form = useForm<FormValues>({
    initialValues: {
      permissions: completePermissions,
      parentRole: role?.parentRole ?? null,
    },
    validate: zodResolver(editorSchema),
  });

  React.useEffect(() => {
    if (role && permissionSetData?.data?.permissions) {
      form.setValues({
        permissions: mergePermissions(permissionSetData.data.permissions, role.permissions ?? {}),
        parentRole: role?.parentRole ?? null,
      });
    } else if (role) {
      form.setValues({
        permissions: role.permissions ?? {},
        parentRole: role?.parentRole ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, opened, permissionSetData]);

  // Close modal when all mutations complete after submission
  React.useEffect(() => {
    if (isSubmitting && !isLoading && !isHierarchyLoading) {
      setIsSubmitting(false);
      onClose();
    }
  }, [isSubmitting, isLoading, isHierarchyLoading, onClose]);

  const handleSubmit = (values: FormValues) => {
    if (!role) return;

    setIsSubmitting(true);

    // Update permissions
    mutate(
      {
        role: role.role,
        permissions: values.permissions,
      }
    );

    // Update parent role if changed
    if (values.parentRole !== role.parentRole) {
      updateHierarchy({
        role: role.role,
        parentRole: values.parentRole ?? null,
      });
    }
  };

  if (!role) return null;

  if (isPermissionSetLoading) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        size="xl"
        title=""
        centered
        withCloseButton={false}
        overlayOpacity={0.5}
        transitionDuration={300}
      >
        <ModalHeader
          title={`Edit ${role.role} Permissions`}
          onClose={onClose}
          ariaLabel="Close edit permissions modal"
        />
        <Center py="lg">
          <Loader />
        </Center>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title=""
      centered
      withCloseButton={false}
      overlayOpacity={0.5}
      transitionDuration={300}
    >
      <ModalHeader
        title={`Edit ${role.role} Permissions`}
        onClose={onClose}
        ariaLabel="Close edit permissions modal"
      />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="md" style={{ position: "relative" }}>
          {/* Loading Overlay */}
          {(isLoading || isHierarchyLoading) && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "8px",
              }}
            >
              <Loader size="lg" />
            </div>
          )}

          {/* Parent Role Selector */}
          <Select
            label="Parent Role"
            placeholder="Select parent role"
            searchable
            clearable
            disabled={isLoading || isHierarchyLoading}
            data={allRoles
              .filter((r) => r.role !== role.role)
              .map((r) => ({
                value: r.role,
                label: `${r.role}${r.isDefault ? " (Default)" : ""}`,
              }))}
            value={form.values.parentRole}
            onChange={(value) => form.setFieldValue("parentRole", value)}
            description="Select which role this role inherits permissions from (optional)"
          />

          <ScrollArea style={{ height: 400, overflow: "auto" }}>
            <Paper
              withBorder
              radius="sm"
              p="sm"
              sx={{ backgroundColor: "#f1f3f5" }}
            >
              {/* Top-level boolean permissions */}
              <Grid mb="md">
                {Object.entries(form.values.permissions)
                  .filter(([_, value]) => typeof value === "boolean")
                  .map(([key, value]) => (
                    <Grid.Col span={4} key={key}>
                      <Switch
                        label={getPermissionLabel(key)}
                        checked={value as boolean}
                        disabled={isLoading || isHierarchyLoading}
                        onChange={(e) =>
                          form.setFieldValue(
                            `permissions.${key}`,
                            e.currentTarget.checked
                          )
                        }
                      />
                    </Grid.Col>
                  ))}
              </Grid>

              {/* Nested permission groups */}
              {Object.entries(form.values.permissions)
                .filter(
                  ([_, value]) => typeof value === "object" && value !== null
                )
                .map(([key, value]) => (
                  <div key={key} style={{ marginBottom: 20 }}>
                    <Text size="sm" weight={600} mt="sm" mb="xs">
                      {getPermissionCategoryLabel(key)}
                    </Text>

                    <Grid>
                      {Object.entries(value as Record<string, unknown>).map(
                        ([subKey, subValue]) => {
                        if (typeof subValue === "object" && subValue !== null) {
                          return (
                            <Grid.Col span={12} key={`${key}.${subKey}`}>
                              <Text
                                size="sm"
                                weight={500}
                                mt="xs"
                                mb="xs"
                                color="dimmed"
                              >
                                {getPermissionLabel(`${key}.${subKey}`)}
                              </Text>
                              <Grid pl="md">
                                {Object.entries(subValue).map(
                                  ([nestedKey, nestedValue]) => (
                                    <Grid.Col
                                      span={4}
                                      key={`${key}.${subKey}.${nestedKey}`}
                                    >
                                      <Switch
                                        label={getPermissionLabel(
                                          `${key}.${subKey}.${nestedKey}`
                                        )}
                                        checked={!!nestedValue}
                                        disabled={isLoading || isHierarchyLoading}
                                        onChange={(e) =>
                                          form.setFieldValue(
                                            `permissions.${key}.${subKey}.${nestedKey}`,
                                            e.currentTarget.checked
                                          )
                                        }
                                      />
                                    </Grid.Col>
                                  )
                                )}
                              </Grid>
                            </Grid.Col>
                          );
                        }

                        return (
                          <Grid.Col span={4} key={`${key}.${subKey}`}>
                            <Switch
                              label={getPermissionLabel(`${key}.${subKey}`)}
                              checked={!!subValue}
                              disabled={isLoading || isHierarchyLoading}
                              onChange={(e) =>
                                form.setFieldValue(
                                  `permissions.${key}.${subKey}`,
                                  e.currentTarget.checked
                                )
                              }
                            />
                          </Grid.Col>
                        );
                      })}
                    </Grid>
                  </div>
                ))}
            </Paper>
          </ScrollArea>

          <Divider my="md" />

          <Group position="right">
            <Button color="gray" onClick={onClose} disabled={isLoading || isHierarchyLoading}>
              Cancel
            </Button>
            <Button type="submit" color="orange" loading={isLoading || isHierarchyLoading} disabled={isLoading || isHierarchyLoading}>
              Save Permissions
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
